/**
 * API functions for losses and delays management
 */
import { authenticatedFetch } from './authApi';

export interface LossDelayRecord {
  id?: number;
  fabrica: string;
  filial: string;
  data: string;
  perda_ou_atraso: string;
  nova_producao_m3: number;
  acoes_delete?: boolean;
  update_date?: string;
}

export interface LossDelayNotification {
  id: string;
  fabrica: string;
  filial: string;
  dia_mes: string;
  tipo: 'Perda' | 'Atraso';
  novo_m3?: number;
  timestamp: string;
  isRead: boolean;
  data: string;
  perda_ou_atraso: string;
  nova_producao_m3: number;
  updated_date: string;
}

export interface LossesApiResponse<T = any> {
  success: boolean;
  status?: string;
  message?: string;
  data?: T;
  error?: string;
  perdas_atrasos?: T;
}

export interface BulkUpdatePayload {
  id?: number;
  fabrica: string;
  filial: string;
  data: string;
  perda_ou_atraso: string;
  nova_producao_m3: number;
}

// API base URL for the production backend (includes /api prefix)
const API_BASE = 'https://dexcobackend-v1-production.up.railway.app/api';

/**
 * Get all losses and delays records for notifications
 */
export const getLossesDelays = async (): Promise<LossesApiResponse<LossDelayRecord[]>> => {
  try {
    console.log('📤 Fetching losses and delays...');
    
    const response = await authenticatedFetch(`${API_BASE}/get_perdas_atrasos/`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const apiResponse = await response.json();
    console.log('🔍 API Response:', apiResponse);
    
    // Handle both response formats: with 'data' or 'perdas_atrasos'
    const data = apiResponse.data || apiResponse.perdas_atrasos;
    
    if (apiResponse.status === 'success' && data) {
      console.log('✅ Losses and delays fetched successfully:', data.length);
      
      return {
        success: true,
        data: data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error fetching losses and delays:', error);
    return {
      success: false,
      error: 'Erro ao carregar perdas e atrasos'
    };
  }
};

/**
 * Create multiple losses and delays records using bulk operations
 * Uses POST method with authentication token
 */
export const createLossesDelaysBulk = async (records: BulkUpdatePayload[]): Promise<LossesApiResponse> => {
  try {
    console.log('📤 Creating losses and delays in bulk...', records.length);
    
    const response = await authenticatedFetch(`${API_BASE}/perdas_atrasos/bulk/`, {
      method: 'POST',
      body: JSON.stringify(records)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const apiResponse = await response.json();
    
    if (apiResponse.status === 'success') {
      console.log('✅ Losses and delays created successfully');
      
      return {
        success: true,
        message: apiResponse.message,
        data: apiResponse.data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error creating losses and delays:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao criar perdas e atrasos'
    };
  }
};

/**
 * UPSERT multiple losses and delays records (create new + update existing)
 */
export const upsertLossesDelaysBulk = async (records: BulkUpdatePayload[]): Promise<LossesApiResponse> => {
  try {
    console.log('📤 Upserting losses and delays in bulk...', records.length);
    
    const response = await authenticatedFetch(`${API_BASE}/perdas_atrasos/bulk/`, {
      method: 'PUT',
      body: JSON.stringify(records)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    console.log('✅ Losses and delays upserted successfully:', responseData);
    
    return {
      success: true,
      data: responseData
    };
  } catch (error) {
    console.error('❌ Error upserting losses and delays:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao fazer upsert de perdas/atrasos'
    };
  }
};

/**
 * Update multiple losses and delays records using bulk operations
 */
export const updateLossesDelaysBulk = async (records: BulkUpdatePayload[]): Promise<LossesApiResponse> => {
  try {
    console.log('📤 Updating losses and delays in bulk...', records.length);
    
    const response = await authenticatedFetch(`${API_BASE}/perdas_atrasos/bulk/`, {
      method: 'PATCH',
      body: JSON.stringify(records)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const apiResponse = await response.json();
    
    if (apiResponse.status === 'success') {
      console.log('✅ Losses and delays updated successfully');
      
      return {
        success: true,
        message: apiResponse.message,
        data: apiResponse.data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error updating losses and delays:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao atualizar perdas e atrasos'
    };
  }
};

/**
 * Delete multiple losses and delays records using bulk operations
 */
export const deleteLossesDelaysBulk = async (ids: number[]): Promise<LossesApiResponse> => {
  try {
    console.log('📤 Deleting losses and delays in bulk...', ids.length);
    
    const response = await authenticatedFetch(`${API_BASE}/perdas_atrasos/bulk/`, {
      method: 'DELETE',
      body: JSON.stringify({ ids })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const apiResponse = await response.json();
    
    if (apiResponse.status === 'success') {
      console.log('✅ Losses and delays deleted successfully');
      
      return {
        success: true,
        message: apiResponse.message,
        data: apiResponse.data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error deleting losses and delays:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao deletar perdas e atrasos'
    };
  }
};

/**
 * Convert API data to notification format
 */
export const convertToNotifications = (records: LossDelayRecord[]): LossDelayNotification[] => {
  return records
    .filter(record => {
      // Only show records that are not hidden (acoes_delete = false) and have data
      return !record.acoes_delete && record.data;
    })
    .map(record => {
      const date = new Date(record.data);
      const day = date.getDate().toString().padStart(2, '0');
      
      // Determine if it's "Perda" or "Atraso" based on perda_ou_atraso field
      const perda_ou_atraso = record.perda_ou_atraso.toLowerCase();
      const isPerda = perda_ou_atraso.includes('perda') || 
                      perda_ou_atraso.includes('loss') ||
                      perda_ou_atraso.includes('material');
      
      // Use update_date if available, otherwise use current time for newly created records
      const timestamp = record.update_date || new Date().toISOString();
      
      return {
        id: record.id?.toString() || Date.now().toString(),
        fabrica: record.fabrica,
        filial: record.filial,
        dia_mes: day,
        tipo: isPerda ? 'Perda' as const : 'Atraso' as const,
        novo_m3: isPerda ? record.nova_producao_m3 : undefined,
        timestamp: timestamp,
        isRead: false,
        data: record.data,
        perda_ou_atraso: record.perda_ou_atraso,
        nova_producao_m3: record.nova_producao_m3,
        updated_date: timestamp
      };
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()); // Most recent first
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};